import type { ReactNode } from 'react';

import { cn } from '@core/lib/cn';

/**
 * The severity trio the brief calls for: green (pass), amber (warn), red (block).
 * `info` is the neutral fourth used for non-verdict guidance.
 */
export type Severity = 'success' | 'warning' | 'danger' | 'info';

export interface AlertProps {
  severity: Severity;
  /** Short verdict line, e.g. "3 pages are lower than 150 DPI". */
  title?: ReactNode;
  children?: ReactNode;
  /** Right-hand action, e.g. a "Fix" or "Retry" button. */
  action?: ReactNode;
  className?: string;
  /**
   * Announce this alert when it appears. Use for validation verdicts and errors;
   * leave off for static guidance, which would otherwise interrupt on every render.
   */
  live?: boolean;
}

const STYLES: Record<Severity, { box: string; icon: string; glyph: string; label: string }> = {
  success: {
    box: 'bg-success-bg border-success-border',
    icon: 'text-success',
    glyph: '✓',
    label: 'Passed',
  },
  warning: {
    box: 'bg-warning-bg border-warning-border',
    icon: 'text-warning',
    glyph: '!',
    label: 'Warning',
  },
  danger: {
    box: 'bg-danger-bg border-danger-border',
    icon: 'text-danger',
    glyph: '×',
    label: 'Problem',
  },
  info: { box: 'bg-info-bg border-info-border', icon: 'text-info', glyph: 'i', label: 'Note' },
};

/**
 * Severity is never communicated by colour alone: each variant carries a glyph
 * and a visually-hidden word ("Warning", "Problem"). A colour-blind user or a
 * screen-reader user gets the same verdict as everyone else (WCAG 1.4.1).
 */
export function Alert({ severity, title, children, action, className, live = false }: AlertProps) {
  const style = STYLES[severity];
  return (
    <div
      className={cn('flex gap-3 rounded-card border p-4', style.box, className)}
      role={live ? 'alert' : undefined}
      aria-live={live ? (severity === 'danger' ? 'assertive' : 'polite') : undefined}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid h-6 w-6 shrink-0 place-items-center rounded-full border border-current text-sm font-bold',
          style.icon,
        )}
      >
        {style.glyph}
      </span>
      <div className="min-w-0 flex-1">
        <span className="sr-only">{style.label}: </span>
        {title ? <p className={cn('font-semibold', style.icon)}>{title}</p> : null}
        {children ? <div className="text-sm text-ink-muted [&>p+p]:mt-2">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0 self-center">{action}</div> : null}
    </div>
  );
}

export interface IssueCardProps {
  severity: Severity;
  /** The rule that fired, e.g. "Low scan resolution". */
  title: ReactNode;
  /** What it means for the print. Customer prose, not a rule id. */
  detail?: ReactNode;
  /** Where it applies, e.g. "Pages 4, 7-9". */
  scope?: ReactNode;
  /** What the customer can do about it. */
  action?: ReactNode;
  className?: string;
}

/**
 * One row of the PDF validation report.
 *
 * Deliberately separate from Alert: a validation issue always has the same four
 * parts (verdict, explanation, which pages, what to do), and rendering them in a
 * fixed order is what makes a list of eight issues scannable instead of a wall of
 * prose. The list around it should be a <ul>; this renders the <li>.
 */
export function IssueCard({ severity, title, detail, scope, action, className }: IssueCardProps) {
  const style = STYLES[severity];
  return (
    <li className={cn('flex gap-3 border-b border-line p-4 last:border-b-0', className)}>
      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full border border-current text-sm font-bold',
          style.icon,
        )}
      >
        {style.glyph}
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">
          <span className="sr-only">{style.label}: </span>
          {title}
        </p>
        {scope ? <p className="mt-0.5 text-sm font-medium text-ink-muted">{scope}</p> : null}
        {detail ? <p className="mt-1 text-sm text-ink-muted">{detail}</p> : null}
        {action ? <div className="mt-2">{action}</div> : null}
      </div>
    </li>
  );
}
